import type { ReactNode } from 'react'
import type { TooltipContentProps } from 'recharts'
import type {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'

interface ChartTooltipProps extends TooltipContentProps<ValueType, NameType> {
  // Lets a chart-specific unit (e.g. "%") be appended without hardcoding
  // it into this shared component.
  formatValue?: (value: ValueType) => ReactNode
  // When the hovered point sits near the chart's right edge, the box has
  // no room to open rightward (it renders under whatever's to the right,
  // e.g. a sibling card). Flip renders it to the LEFT of the anchor with
  // the arrow on the right edge, so the arrow still points at the data.
  flip?: boolean
}

// Tooltip pattern (Light variant): white bg + shadow-lg + arrow pointing
// at the data point + Gray/700 text — not the recharts/antd default styling.
export default function ChartTooltip({
  active,
  label,
  payload,
  formatValue = (value) => value,
  flip = false,
}: ChartTooltipProps) {
  if (!active || !payload?.length) {
    return null
  }

  return (
    <div
      className="relative"
      style={
        flip
          ? { transform: 'translateX(calc(-100% - 12px))' }
          : { marginLeft: 8 }
      }
    >
      <span
        className={`absolute top-1/2 w-3 h-3 bg-white -translate-y-1/2 rotate-45 shadow-lg ${
          flip ? '-right-1.5' : '-left-1.5'
        }`}
      />
      <div className="relative bg-white rounded-md shadow-lg px-3 py-2 text-xs text-gray-700">
        {label && <p className="font-medium text-gray-900 mb-1">{label}</p>}
        {payload.map((entry, index) => (
          <p key={index} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full inline-block"
              style={{ backgroundColor: entry.color }}
            />
            {entry.name}:{' '}
            <span className="font-medium">
              {formatValue(entry.value as ValueType)}
            </span>
          </p>
        ))}
      </div>
    </div>
  )
}
